import React, { useCallback, useEffect, useRef, useState, useMemo, useLayoutEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import mapboxgl from 'mapbox-gl'
import BaseMap from './BaseMap'
import LocationSearch from './LocationSearch'
import RadiusCircle from './RadiusCircle'
import { theme } from '../../config/theme'

const EventMap = ({ 
  className = '',
  eventLocation,
  radius,
  onLocationSelect,
  readOnly = false,
  shouldFitBounds = false
}) => {
  console.log('EventMap component rendering with props:', { eventLocation, radius, shouldFitBounds })
  
  const marker = useRef(null)
  const [searchError, setSearchError] = useState(null)
  const [mapInstance, setMapInstance] = useState(null)
  const [shouldCenterOnNextUpdate, setShouldCenterOnNextUpdate] = useState(false)
  const previousLocationRef = useRef(null)
  
  // Make form context optional
  const formContext = useFormContext()
  const setValue = formContext?.setValue
  const watch = formContext?.watch
  const formState = formContext?.formState
  const location = watch?.('location')

  // Get the current radius value (either from prop or form)
  const currentRadius = radius || watch?.('radius') || 2

  // Determine center for map and radius circle
  // For event editing, prioritize form location over initial eventLocation
  const mapCenter = useMemo(() => {
    if (location?.latitude && location?.longitude) {
      return [parseFloat(location.longitude), parseFloat(location.latitude)]
    }
    if (eventLocation) {
      return [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]
    }
    return null
  }, [location, eventLocation])

  // Handle map click for location selection
  const handleMapClick = useCallback(async (e) => {
    if (readOnly) return

    const { lng, lat } = e.lngLat
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&types=address&limit=1&autocomplete=false`
      )
      const data = await response.json()
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0]
        const locationData = {
          address: feature.place_name.split(',')[0].trim(),
          city: feature.context?.find(c => c.id.startsWith('place'))?.text || '',
          state: feature.context?.find(c => c.id.startsWith('region'))?.short_code?.replace(/^US-/, '') || feature.context?.find(c => c.id.startsWith('region'))?.text || '',
          zip_code: feature.context?.find(c => c.id.startsWith('postcode'))?.text || '',
          latitude: lat.toString(),
          longitude: lng.toString()
        }
        
        if (onLocationSelect) {
          onLocationSelect(locationData)
        }
        if (setValue) {
          setValue('location', locationData, { shouldValidate: true })
        }
        
        // Mark that we should center on this update since user actively clicked
        setShouldCenterOnNextUpdate(true)
        updateMarker(lng, lat)
      }
    } catch (error) {
      console.error('Error geocoding location:', error)
    }
  }, [readOnly, onLocationSelect, setValue, currentRadius])

  // Handle location search selection
  const handleLocationSearchSelect = useCallback((locationData, error) => {
    if (error) {
      setSearchError(error)
      return
    }
    
    setSearchError(null)
    
    if (locationData) {
      if (onLocationSelect) {
        onLocationSelect(locationData)
      }
      if (setValue) {
        setValue('location', locationData, { shouldValidate: true })
      }
      
      // Mark that we should center on this update since user actively searched
      setShouldCenterOnNextUpdate(true)
      
      // Don't call updateMarker here - let the useEffect handle it
      // This prevents double calls to updateMarker
    }
  }, [onLocationSelect, setValue])

  // Update marker position
  const updateMarker = useCallback((lng, lat) => {
    if (!mapInstance) return

    console.log('updateMarker called with:', lng, lat, 'shouldCenterOnNextUpdate:', shouldCenterOnNextUpdate)

    // Remove existing marker
    if (marker.current) {
      marker.current.remove()
      marker.current = null
    }

    // Create new marker
    marker.current = new mapboxgl.Marker({ color: theme.colors.primary[500] })
      .setLngLat([lng, lat])
      .addTo(mapInstance)

    // Fit bounds if requested OR if user actively changed location
    if (shouldFitBounds || shouldCenterOnNextUpdate) {
      const radiusForBounds = radius || watch?.('radius') || 2
      const radiusInDegrees = radiusForBounds / 69 // Convert miles to degrees (approximate)
      
      // Create bounds that show the entire radius around the marker
      const bounds = [
        [lng - radiusInDegrees, lat - radiusInDegrees], // Southwest corner
        [lng + radiusInDegrees, lat + radiusInDegrees]  // Northeast corner
      ]

      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        mapInstance.fitBounds(bounds, {
          padding: 20, // Reasonable padding for visual buffer
          maxZoom: 15  // Prevent zooming in too close for small radius
        })
      })
      
      // Reset the flag after centering
      setShouldCenterOnNextUpdate(false)
    }
  }, [mapInstance, currentRadius, shouldFitBounds, radius, watch, shouldCenterOnNextUpdate])

  // Handle map ready
  const handleMapReady = useCallback((mapInstance) => {
    setMapInstance(mapInstance)
  }, [])

  // Initialize marker when map is ready and we have location data
  useEffect(() => {
    if (!mapInstance) return

    if (mapCenter) {
      const [lng, lat] = mapCenter
      console.log('mapCenter effect triggered, updating marker to:', lng, lat)
      updateMarker(lng, lat)
    }
  }, [mapInstance, mapCenter, updateMarker])

  // Update marker when location changes
  useEffect(() => {
    if (!mapInstance || !location?.latitude || !location?.longitude) return

    const lng = parseFloat(location.longitude)
    const lat = parseFloat(location.latitude)
    
    // Check if location actually changed
    const currentLocationKey = `${lng},${lat}`
    if (previousLocationRef.current === currentLocationKey) {
      console.log('Location unchanged, skipping marker update')
      return // Location hasn't actually changed
    }
    
    console.log('Location changed from', previousLocationRef.current, 'to', currentLocationKey)
    previousLocationRef.current = currentLocationKey
    
    if (!isNaN(lng) && !isNaN(lat)) {
      updateMarker(lng, lat)
    }
  }, [location, mapInstance, updateMarker])

  // Get center for map initialization - prefer eventLocation initially, then form location
  // Memoize this so it doesn't change on every render
  const initialCenter = useMemo(() => {
    if (eventLocation) {
      return [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]
    }
    if (location?.latitude && location?.longitude) {
      return [parseFloat(location.longitude), parseFloat(location.latitude)]
    }
    return [-98.5795, 39.8283] // Default center of US
  }, [eventLocation]) // Only depend on eventLocation, not location (which changes frequently)
  
  const initialZoom = (eventLocation || location?.latitude) ? 12 : 3

  return (
    <div className={className}>
      {!readOnly && (
        <div className="mb-4">
          <LocationSearch
            onLocationSelect={handleLocationSearchSelect}
            disabled={readOnly}
          />
        </div>
      )}

      <BaseMap
        center={initialCenter}
        zoom={initialZoom}
        onClick={handleMapClick}
        onMapReady={handleMapReady}
      >
        <RadiusCircle
          center={mapCenter}
          radius={currentRadius}
          visible={true}
        />
      </BaseMap>

      {location?.address && !readOnly && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Selected location:</span>{' '}
            {location.address}, {location.city}, {location.state} {location.zip_code}
          </p>
        </div>
      )}

      {searchError && !readOnly && (
        <p className="mt-2 text-sm text-red-600">
          {searchError}
        </p>
      )}

      {formState?.errors?.['location.latitude'] || formState?.errors?.['location.longitude'] ? (
        <p className="mt-2 text-sm text-red-600">
          Please select a location on the map
        </p>
      ) : null}
    </div>
  )
}

export default EventMap